import { WechatProfitSharingTypeEnum } from '@lighthouse/core'
import type { WeChatAddProfitSharingReceiverActionConfig } from '@lighthouse/shared'
import { useAtomData } from '@lighthouse/shared'
import { wechatMerchantCardLabel } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import { PROFIT_SHARING_RELATION_TYPE_OPTIONS } from '../../../NodeSetting/Actions/WeChatProfitSharingActionConfigure/constants'
import * as CM from '../styles'
import { TakeVariableWithText, useTakeVariableWithText } from '../TakeVariableWithText'
import * as SC from './style'

type WeChatAddProfitSharingReceiverCardProps = {
    config: WeChatAddProfitSharingReceiverActionConfig
}

const WeChatAddProfitSharingReceiverCard: React.FC<WeChatAddProfitSharingReceiverCardProps> = ({ config }) => {
    const { type, account, name, relationType, customRelation } = config

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const userOption = useGetUserRecordOptions()

    const accountData = useTakeVariableWithText({ dataSourceList, value: account })
    const nameData = useTakeVariableWithText({ dataSourceList, value: name })
    const customRelationData = useTakeVariableWithText({ dataSourceList, value: customRelation })

    const merchantLabel = useMemo(
        () => wechatMerchantCardLabel(otherPlatformList, config?.paymentAccountId),
        [otherPlatformList, config?.paymentAccountId]
    )
    if (!merchantLabel) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <SC.TypeName>{merchantLabel}</SC.TypeName>
            <SC.Item>
                <SC.TypeName>分账接收方类型</SC.TypeName>
                <SC.Value>{type === WechatProfitSharingTypeEnum.MERCHANT_ID ? '商户' : '个人'}</SC.Value>
            </SC.Item>

            {accountData.data.objects.length > 0 && (
                <SC.Item>
                    <SC.TypeName>分账接收方账号</SC.TypeName>
                    <SC.Value>
                        <TakeVariableWithText
                            nodes={accountData.data.objects}
                            upstreamOptions={accountData.upstreamOptions}
                            userOption={userOption}
                        />
                    </SC.Value>
                </SC.Item>
            )}

            {nameData.data.objects.length > 0 && (
                <SC.Item>
                    <SC.TypeName>分账接收方全称</SC.TypeName>
                    <SC.Value>
                        <TakeVariableWithText
                            nodes={nameData.data.objects}
                            upstreamOptions={nameData.upstreamOptions}
                            userOption={userOption}
                        />
                    </SC.Value>
                </SC.Item>
            )}

            <SC.Item>
                <SC.TypeName>与分账接收方的关系类型</SC.TypeName>
                <SC.Value>{PROFIT_SHARING_RELATION_TYPE_OPTIONS.find(item => item.value === relationType)?.label}</SC.Value>
            </SC.Item>

            {customRelationData.data.objects.length > 0 && (
                <SC.Item>
                    <SC.TypeName>自定义的分账关系</SC.TypeName>
                    <SC.Value>
                        <TakeVariableWithText
                            nodes={customRelationData.data.objects}
                            upstreamOptions={customRelationData.upstreamOptions}
                            userOption={userOption}
                        />
                    </SC.Value>
                </SC.Item>
            )}
        </CM.CardItemContainer>
    )
}

export default WeChatAddProfitSharingReceiverCard
